import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { Description, Search } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';

interface AttachCandidate {
  id: string;
  title: string;
  originalName?: string;
  documentType?: string;
  category?: string;
  createdAt?: string;
  case?: { id: string; caseNumber?: string; title?: string } | null;
  client?: { id: string; name?: string } | null;
}

interface AttachExistingDocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber?: string;
  /** Matter's client, so filing another client's document can be caught. */
  clientId?: string | null;
  clientName?: string;
  /** Pre-select the folder the documents should land in. */
  folderId?: string | null;
  onSuccess?: () => void;
}

const PAGE_SIZE = 50;

const AttachExistingDocumentsDialog: React.FC<AttachExistingDocumentsDialogProps> = ({
  open,
  onClose,
  caseId,
  caseNumber,
  clientId = null,
  clientName,
  folderId = null,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  // Off by default so the common case — filing documents that were uploaded
  // without a matter — cannot accidentally pull files out of another matter.
  const [includeOtherCases, setIncludeOtherCases] = useState(false);
  const [confirmMove, setConfirmMove] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelected([]);
      setIncludeOtherCases(false);
      setConfirmMove(false);
    }
  }, [open]);

  useEffect(() => {
    setSelected([]);
    setConfirmMove(false);
  }, [includeOtherCases]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['attachable-documents', caseId, search, includeOtherCases],
    enabled: open && Boolean(caseId),
    queryFn: async () => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');
      const params = new URLSearchParams({
        isTemplate: 'false',
        limit: String(PAGE_SIZE),
      });
      if (includeOtherCases) params.set('excludeCaseId', caseId);
      else params.set('unassigned', 'true');
      if (search.trim()) params.set('q', search.trim());

      const response = await fetch(`/api/documents?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to load documents.'));
      }
      return response.json();
    },
  });

  const candidates: AttachCandidate[] = useMemo(
    () => (data?.data?.documents || []) as AttachCandidate[],
    [data]
  );
  const total: number = data?.data?.pagination?.total ?? candidates.length;

  const attachMutation = useMutation({
    mutationFn: async ({ documentIds, allowReassign }: { documentIds: string[]; allowReassign: boolean }) => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');
      const response = await fetch(`/api/case-documents/${caseId}/documents/attach`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ documentIds, allowReassign, folderId: folderId || undefined }),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to add documents to this matter.'));
      }
      return response.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] });
      queryClient.invalidateQueries({ queryKey: ['matter-drive'] });
      queryClient.invalidateQueries({ queryKey: ['matter-drive-tree'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documents-count'] });
      queryClient.invalidateQueries({ queryKey: ['attachable-documents'] });
      toast.success(result?.message || 'Documents added to this matter');
      onSuccess?.();
      onClose();
    },
    onError: (err: Error) => {
      toast.error(toUserErrorMessage(err, 'Failed to add documents to this matter.'));
    },
  });

  const toggle = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectedDocs = candidates.filter((doc) => selected.includes(doc.id));
  const movingFromAnotherCase = selectedDocs.some((doc) => doc.case?.id && doc.case.id !== caseId);
  // Filing one client's document into another client's matter is the mistake
  // with real consequences here, so name it before the move can go through.
  const otherClientDocs = selectedDocs.filter(
    (doc) => doc.client?.id && clientId && doc.client.id !== clientId
  );
  const needsConfirmation = movingFromAnotherCase || otherClientDocs.length > 0;

  const handleAttach = () => {
    if (selected.length === 0) return;
    if (needsConfirmation && !confirmMove) return;
    attachMutation.mutate({ documentIds: selected, allowReassign: movingFromAnotherCase });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Add Existing Documents{caseNumber ? ` to ${caseNumber}` : ''}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select documents already in your firm&apos;s library and file them into this matter.
        </Typography>

        <TextField
          fullWidth
          size="small"
          label="Search documents"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ mb: 1 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={includeOtherCases}
              onChange={(e) => setIncludeOtherCases(e.target.checked)}
              size="small"
            />
          }
          label="Also show documents filed to other matters (moving them re-files them here)"
        />

        {needsConfirmation && (
          <Alert severity={otherClientDocs.length > 0 ? 'error' : 'warning'} sx={{ mb: 2 }}>
            {otherClientDocs.length > 0 && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                {otherClientDocs.length === 1
                  ? `"${otherClientDocs[0].title || 'This document'}" belongs to ${otherClientDocs[0].client?.name || 'another client'}`
                  : `${otherClientDocs.length} selected documents belong to a different client`}
                , not {clientName || 'this matter\'s client'}. Filing it here transfers it to this
                client and removes it from the other client&apos;s portal.
              </Typography>
            )}
            {movingFromAnotherCase && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Some selected documents are filed to another matter. Adding them here moves them out
                of that matter. The change is recorded in the audit log.
              </Typography>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={confirmMove}
                  onChange={(e) => setConfirmMove(e.target.checked)}
                />
              }
              label="I have checked these documents and want to move them"
            />
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {toUserErrorMessage(error as Error, 'Failed to load documents.')}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : candidates.length === 0 ? (
          <Alert severity="info">
            {includeOtherCases
              ? 'No other documents were found in the firm library.'
              : 'Every document in the library is already filed to a matter. Tick the box above to move one here.'}
          </Alert>
        ) : (
          <>
            <List dense sx={{ maxHeight: 380, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {candidates.map((doc) => (
                <ListItemButton key={doc.id} onClick={() => toggle(doc.id)} selected={selected.includes(doc.id)}>
                  <ListItemIcon>
                    <Checkbox edge="start" checked={selected.includes(doc.id)} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Description fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.title || doc.originalName || 'Untitled document'}
                    secondary={
                      <Box component="span" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {doc.documentType && (
                          <Chip size="small" label={doc.documentType.replace(/_/g, ' ')} variant="outlined" />
                        )}
                        <Chip
                          size="small"
                          variant="outlined"
                          color={doc.client?.id && clientId && doc.client.id !== clientId ? 'error' : 'default'}
                          label={doc.client?.name || 'No client'}
                        />
                        {doc.case?.id ? (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={`In ${doc.case.caseNumber || 'another matter'}`}
                          />
                        ) : (
                          <Chip size="small" color="default" variant="outlined" label="Unfiled" />
                        )}
                        {doc.createdAt && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {new Date(doc.createdAt).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
            {total > candidates.length && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Showing the {candidates.length} most recent of {total}. Use search to narrow the list.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={attachMutation.isPending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAttach}
          disabled={
            selected.length === 0
            || attachMutation.isPending
            || (needsConfirmation && !confirmMove)
          }
          startIcon={attachMutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {selected.length > 0 ? `Add ${selected.length} Document${selected.length === 1 ? '' : 's'}` : 'Add Documents'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachExistingDocumentsDialog;
